import React from 'react';
import { PILL_BASE, pill } from './Sheet';
import { PinOnlyIcon, ArrowDownIcon } from '../canvas/icons';
import { RING } from '../../wall/wallTokens';
import { safePad } from '../../../utils/safeArea';

import ShareTemplatePicker from '../../share/ShareTemplatePicker';

/**
 * 03 · SEND (handoff v3 §6): WHO SEES IT — any of YOUR CITY · FRIENDS · CLOSE FRIENDS — the
 * clubs to post to as well, the ring-outlined OPEN A PORTAL, and the summary line under it. A
 * city pill opens WHERE ARE YOU?; LAST LOOK ↓ collapses the panel to a WHO SEES IT ↑ pill so
 * the whole Portal shows.
 */
export const AUDIENCES = [
  { key: 'town', label: 'YOUR CITY', sub: (city) => `The whole of ${city}, granted by geography.` },
  { key: 'friends', label: 'FRIENDS', sub: () => 'People you added. They see it on their rail.' },
  { key: 'close', label: 'CLOSE FRIENDS', sub: () => 'Your inner circle only.' },
];
export const CLUB_TINTS = ['#A8D8EA', '#FAE1B4', '#FFCCE6', '#C6B8FF', '#22ECFB', '#FCAA6D'];

export const JOIN_MODES = [
  { key: 'open', label: 'OPEN', sub: 'Everyone is welcome' },
  { key: 'curated', label: 'CURATED', sub: 'They ask, you pick' },
];

/** `YOUR CITY + FRIENDS · CURATED · 2 CLUBS · NOW → 4H`. */
export function sendSummary(aud, clubCount, windowLabel, mode = 'open') {
  return [
    AUDIENCES.filter((a) => aud.includes(a.key)).map((a) => a.label).join(' + ') || null,
    mode === 'curated' ? 'CURATED' : 'OPEN',
    clubCount ? `${clubCount} CLUB${clubCount > 1 ? 'S' : ''}` : null,
    windowLabel,
  ].filter(Boolean).join(' · ');
}

export function audienceLine(aud, clubCount, city) {
  if (aud.length) return AUDIENCES.filter((a) => aud.includes(a.key)).map((a) => a.sub(city)).join(' ');
  return clubCount ? 'Only the clubs you pick below.' : 'Pick at least one place to post it.';
}

export default function SendPanel({
  aud = [],
  onAud,
  clubs = [],
  clubIds = [],
  onClubIds,
  cityLabel = '',
  onOpenCity,
  peek = false,
  onTogglePeek,
  onSend,
  /** A Portal with no picture and no words: the button takes you back to write them. */
  needsWords = false,
  sending = false,
  windowLabel = 'NOW → 4H',
  joinMode = 'open',
  onJoinMode = null,
  /** Plan detection (Oct 2026): the chat it was filled from, the people in it, and who's invited. */
  fromChat = null,
  inviteIds = [],
  onInviteIds = null,
  onOpenSources = null,
  /** The card this Portal shares as: null follows the admin's default. */
  shareTemplate = 'ring',
  onShareTemplate = null,
  renderCardPreview = null,
}) {
  const toggleInvite = (id) => onInviteIds?.(inviteIds.includes(id) ? inviteIds.filter((x) => x !== id) : [...inviteIds, id]);
  const city = (cityLabel || 'your city').split(',')[0].trim();
  const canSend = (aud.length > 0 || clubIds.length > 0) && !sending;
  const toggleAud = (k) => onAud(aud.includes(k) ? aud.filter((x) => x !== k) : [...aud, k]);
  const toggleClub = (id) => onClubIds(clubIds.includes(id) ? clubIds.filter((x) => x !== id) : [...clubIds, id]);
  const smallPill = { ...PILL_BASE, display: 'flex', alignItems: 'center', gap: 6, height: 28, padding: '0 12px', background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 10, letterSpacing: '0.12em', whiteSpace: 'nowrap' };

  if (peek) {
    return (
      <button type="button" onClick={onTogglePeek} data-testid="send-peek" style={{ ...PILL_BASE, position: 'absolute', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 6, height: 40, padding: '0 18px', background: '#fff', color: '#000', fontSize: 11, letterSpacing: '0.14em', whiteSpace: 'nowrap' }}>
        WHO SEES IT ↑
      </button>
    );
  }

  return (
    <div className="font-mono text-white" data-testid="send-panel" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 6, background: '#000', padding: safePad('16px 16px 14px'), display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)', whiteSpace: 'nowrap' }}>WHO SEES IT</span>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.45)', whiteSpace: 'nowrap' }}>PICK ANY</span>
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {fromChat && (
            <button type="button" onClick={onOpenSources} className="prc-pill" style={{ ...smallPill, border: '2px solid transparent', background: `linear-gradient(#000,#000) padding-box, ${RING} border-box`, fontSize: 9, padding: '0 10px' }} data-testid="from-chat-pill">
              FROM {(fromChat.name || 'THE CHAT').toUpperCase()}
            </button>
          )}
          <button type="button" onClick={onOpenCity} className="prc-pill" style={smallPill} data-testid="city-pill"><PinOnlyIcon />{cityLabel ? city.toUpperCase() : 'PICK A CITY'}</button>
          <button type="button" onClick={onTogglePeek} className="prc-pill" style={smallPill} data-testid="last-look">LAST LOOK<ArrowDownIcon /></button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 6 }}>
        {AUDIENCES.map((a) => {
          const on = aud.includes(a.key);
          return (
            <button key={a.key} type="button" aria-pressed={on} onClick={() => toggleAud(a.key)} style={{ ...PILL_BASE, height: 40, padding: '0 6px', ...pill(on), fontSize: 10, letterSpacing: '0.1em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.label}</button>
          );
        })}
      </div>
      <span style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,.6)' }} data-testid="audience-line">{audienceLine(aud, clubIds.length, city)}</span>

      <span style={{ marginTop: 2, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)' }}>WHO CAN JOIN</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 6 }} data-testid="join-mode">
        {JOIN_MODES.map((m) => {
          const on = m.key === joinMode;
          return (
            <button key={m.key} type="button" aria-pressed={on} onClick={() => onJoinMode?.(m.key)} style={{ ...PILL_BASE, height: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '0 10px', ...pill(on) }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em' }}>{m.label}</span>
              <span style={{ fontSize: 9, letterSpacing: '0.06em', opacity: 0.7, fontWeight: 400 }}>{m.sub}</span>
            </button>
          );
        })}
      </div>


      {onShareTemplate && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)' }}>SHARE CARD</span>
          <ShareTemplatePicker kind="signal" value={shareTemplate} onChange={onShareTemplate} size={52} renderPreview={renderCardPreview} />
        </div>
      )}

      <span style={{ marginTop: 2, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)' }}>
        ALSO POST TO YOUR CLUBS <span style={{ color: 'rgba(255,255,255,.5)' }}>· {clubIds.length ? `${clubIds.length} PICKED` : 'OPTIONAL'}</span>
      </span>
      <div className="prc-row" style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '0 -16px', padding: '0 16px' }} data-testid="club-row">
        {clubs.length === 0 && <span style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,.45)', padding: '9px 0' }}>YOU’RE NOT IN ANY CLUBS YET.</span>}
        {clubs.map((c, i) => {
          const on = clubIds.includes(c.id);
          const img = c.logoUrl || c.imageUrl || c.avatarUrl || null;
          return (
            <button key={c.id} type="button" role="checkbox" aria-checked={on} onClick={() => toggleClub(c.id)} style={{ ...PILL_BASE, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 14px 0 4px', ...pill(on), fontSize: 11, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: CLUB_TINTS[i % CLUB_TINTS.length], color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, overflow: 'hidden' }}>
                {img ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (c.name || '?').charAt(0).toUpperCase()}
              </span>
              {c.name}
            </button>
          );
        })}
      </div>

      {fromChat && (fromChat.people || []).length > 0 && (
        <>
          <span style={{ marginTop: 2, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)' }}>
            INVITE <span style={{ color: 'rgba(255,255,255,.5)' }}>· {inviteIds.length} FROM THE CHAT</span>
          </span>
          <div className="prc-row" style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '0 -16px', padding: '0 16px' }} data-testid="invite-row">
            {fromChat.people.map((p, i) => {
              const on = inviteIds.includes(p.userId);
              return (
                <button key={p.userId} type="button" role="checkbox" aria-checked={on} onClick={() => toggleInvite(p.userId)} style={{ ...PILL_BASE, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 12px 0 4px', ...pill(on), fontSize: 10, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                  <span style={{ width: 28, height: 28, borderRadius: '50%', background: CLUB_TINTS[(i + 2) % CLUB_TINTS.length], color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, overflow: 'hidden' }}>
                    {p.avatarUrl ? <img src={p.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p.name || '?').charAt(0).toUpperCase()}
                  </span>
                  {(p.name || '').split(' ')[0].toUpperCase()}
                </button>
              );
            })}
          </div>
        </>
      )}

      <button
        type="button"
        onClick={() => canSend && onSend()}
        aria-disabled={!canSend}
        data-testid="send-portal"
        style={{ marginTop: 4, width: '100%', height: 52, opacity: canSend ? 1 : 0.4, background: `linear-gradient(#000,#000) padding-box, ${RING} border-box`, color: '#fff', border: '2px solid transparent', borderRadius: 999, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, letterSpacing: '0.16em', cursor: canSend ? 'pointer' : 'default', transition: 'opacity .2s' }}
      >
        {sending ? 'OPENING…' : 'OPEN A PORTAL'}
      </button>
      {needsWords && <span style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: '#fff' }} data-testid="send-needs-words">ADD SOME WORDS OR A PHOTO FIRST</span>}
      <span style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.6)' }} data-testid="send-summary">{[sendSummary(aud, clubIds.length, windowLabel, joinMode), fromChat && inviteIds.length ? `${inviteIds.length} INVITED` : null].filter(Boolean).join(' · ')}</span>
    </div>
  );
}
